import { useState } from "react";
import { SecaoCard } from "@/components/padroes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { formatCents } from "@/lib/format";
import type { StatusEmissao } from "@/data/folha";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Mail } from "lucide-react";

interface Props {
  folhaId: string;
  status: StatusEmissao;
  podeCobrar: boolean;
  onAtualizar: () => void;
  /** Rota do lembrete; padrão = folha de pagamento. */
  endpointCobrar?: string;
  /** Variável que falta quando o e-mail não está configurado. */
  templateEmail?: string;
}

export const StatusEmissaoPainel = ({
  folhaId,
  status,
  podeCobrar,
  onAtualizar,
  endpointCobrar,
  templateEmail = "SENDGRID_TEMPLATE_AVISO_NF",
}: Props) => {
  const [cobrando, setCobrando] = useState(false);

  const cobrar = async () => {
    setCobrando(true);
    try {
      const r = await api.post<{ enviados: number; falhas: number; configurado: boolean; pendentes: number }>(
        endpointCobrar ?? `/folhas/${folhaId}/cobrar-pendentes`,
        {},
      );
      if (!r.configurado) {
        toast.warning("Envio de e-mail não configurado", {
          description: `Falta definir ${templateEmail} no ambiente.`,
        });
      } else {
        toast.success(`${r.enviados} lembrete(s) enviado(s)`);
      }
      onAtualizar();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao cobrar");
    } finally {
      setCobrando(false);
    }
  };

  if (!status.fechada) {
    return (
      <div className="rounded-xl border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
        O acompanhamento da emissão começa quando a competência é fechada — é o fechamento que
        avisa os colaboradores.
      </div>
    );
  }

  const progresso = status.total === 0 ? 0 : Math.round((status.emitiram / status.total) * 100);

  return (
    <SecaoCard
      semPadding
      titulo={`${status.emitiram} de ${status.total} já emitiram a nota`}
      descricao={
        <span className="mt-1 block h-1 w-56 overflow-hidden rounded-full bg-muted">
          <span className="block h-full bg-primary transition-all" style={{ width: `${progresso}%` }} />
        </span>
      }
      acoes={
        podeCobrar &&
        status.pendentes > 0 && (
          <Button size="sm" onClick={() => void cobrar()} disabled={cobrando}>
            {cobrando ? <Loader2 className="animate-spin" /> : <Mail />}
            Cobrar {status.pendentes} pendente(s)
          </Button>
        )
      }
    >
        <Table className="min-w-[680px]">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5 sm:pl-6">Colaborador</TableHead>
              <TableHead className="text-right">Valor da folha</TableHead>
              <TableHead>NF</TableHead>
              <TableHead>Situação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {status.itens.map((i) => (
              <TableRow key={i.colaboradorId} className={i.nfStatus === null ? "bg-amber-500/[.05]" : ""}>
                <TableCell className="pl-5 sm:pl-6">
                  <div className="font-medium">{i.nome}</div>
                  <div className="text-xs text-muted-foreground">{i.email}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCents(i.liquido)}</TableCell>
                <TableCell>
                  {i.nfNumero ?? <span className="text-muted-foreground">—</span>}
                  {i.valorDivergente && i.nfValor !== null && (
                    <div className="text-xs text-destructive">
                      emitida por {formatCents(i.nfValor * 100)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {i.nfStatus === null ? (
                    <Badge variant="outline" className="text-amber-700 dark:text-amber-400">
                      Não emitiu
                    </Badge>
                  ) : i.valorDivergente ? (
                    <span className="inline-flex items-center gap-1 text-sm text-destructive">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Valor divergente
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {i.nfStatus}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
    </SecaoCard>
  );
};
